import create, { UseStore } from "zustand";
import { fetchRequest, Request, RequestProps } from "./";
import {
  LocationDepartmentAccount,
  Visit,
  visitWorkflowOwner,
  WorksheetSummary,
} from "./domain";
import { showToast } from "./toast";
import { currentWorkflow } from "./workflow-api";
import { worksheetMessage, WorksheetResult } from "./worksheet-service";

const service = "coding-queue";

export interface VisitSortField {
  fieldName: string;
  sortOrder?: 1 | -1;
}

export interface VisitSearchField extends VisitSortField {
  sortable?: boolean;
  isArray?: boolean;
  startDate?: string;
  endDate?: string;
  stringFilter?: string | string[] | any;
  integerFilter?: number;
  floatFilter?: number;
}

function searchField(
  fieldName: string,
  isArray?: boolean,
  field?: Partial<VisitSearchField>
): VisitSearchField {
  return { ...{ fieldName, isArray }, ...field };
}

export const locationField = searchField("locationCode");
export const visitAccountField = searchField("accountNumber");
export const registrationDateField = searchField("registrationDate");
export const serviceDateField = searchField("wsSummaries.0.serviceDate", true);
export const mrnField = searchField("mrn");
export const visitFirstDateInCodingField = searchField("firstDateInCoding");
export const worksheetStateField = searchField("wsSummaries.state", true);
export const worksheetIdField = searchField("wsSummaries.worksheetId", true);
export const visitDepartmentField = searchField(
  "wsSummaries.departmentCode",
  true
);
export const ownerFirstField = searchField("wsSummaries.auditId", true);
export const ownerLastField = searchField(
  "wsSummaries.owningProvider.lastName",
  true
);
export const patientFirstField = searchField("patient.firstName");
export const patientLastField = searchField("patient.lastName");
export const visitInsuranceField = searchField(
  "insurances.0.companyName",
  true
);

export const flagField = searchField("wsSummaries.flags.code");
export const flagDescriptionField = searchField("wsSummaries.flags.description");
export const flagCommentsField = searchField("wsSummaries.flags.comments");
export const flagEmployeeLastField = searchField("wsSummaries.flags.employee.lastName");

export const chartOwnerLastField = searchField(
  "wsSummaries.workflowOwners.lastName",
  true //
);
export const auditIdField = searchField("wsSummaries.auditId", true);

export const auditTypeField = searchField("wsSummaries.auditType", true);

export const worksheetTypeField = searchField(
  "wsSummaries.worksheetType",
  true
);

export const auditRuleCreatedWhoField = searchField(
  "wsSummaries.auditInfo.auditRules.auditRuleCreateWho",
  true
);

//Search fields for My Audit
export const worksheetAuditDateField = searchField("createdLocal");
export const auditReviewIdField = searchField("auditId");
export const auditReviewAuditorField = searchField("auditor.lastName");
export const auditReviewAuditedField = searchField("audited.lastName");
export const auditReviewProviderField = searchField("owningProvider.lastName");
export const auditReviewAccountField = searchField("accountNumber");
export const auditReviewLocationField = searchField("locationCode");
export const auditReviewDepartmentField = searchField("departmentCode");
export const auditWorksheetTypeField = searchField("worksheetType");
export const auditServiceDateField = searchField("serviceDate");

export type SearchPredicate = "AND" | "OR";
export interface Filters {
  predicate: SearchPredicate;
  fields: VisitSearchField[];
}

export type VisitSearchQuery = {
  locationCode?: string;
  departmentCode?: string;
  pageNumber?: number;
  page?: number;
  pageSize?: number;
  queryString?: string;
  filters?: Filters;
  sort?: VisitSortField[];
  auditId?: string | string[];
  providerId?: string | string[];
  auditedId?: string | string[];
  startDate?: string;
  endDate?: string;
};

export interface VisitSearchResult {
  totalResults?: number;
  pageSize?: number;
  pageNumber?: number;
  searchHandle?: string;
  entries?: Visit[];
}

const emptyResult: VisitSearchResult = {
  entries: [],
  totalResults: 0,
  pageSize: 0,
  pageNumber: 0,
};

export type VisitResult = WorksheetResult<VisitSearchResult>;

export const getVisitRequest = fetchRequest<VisitSearchQuery, VisitResult>({
  method: "post",
  body: (query: VisitSearchQuery) => {
    if (query.filters && query.filters.fields.length) {
      query.filters.predicate = "AND";

      let fields = [];
      query.filters.fields.forEach((v) => {
        v.stringFilter = v.stringFilter && v.stringFilter.trim();

        if (v.stringFilter?.length && v.stringFilter.indexOf(",") > 0) {
          const stringFilters = v.stringFilter
            .split(",")
            .map((a: string) => a.trim());

          fields.push({ ...v, ...{ stringFilter: stringFilters } });
        } else {
          fields.push(v);
        }
        query.filters.fields = fields;
      });
    } else {
      query.filters = undefined;
    }

    // console.log('VisitSearchQuery', query);
    // TODO: Remove. The backend should allow all custom workflows instead of hard coding exceptions to the rule.
    let workflow = currentWorkflow();
    if (workflow === "auditReviewStd") {
      workflow = "auditStd";
    }
    return worksheetMessage(
      { ...{ pageNumber: 0, pageSize: 20 }, ...query },
      "searchcodingqueue",
      service,
      workflow
    );
  },
});

function request<T = any, U = any>(
  props: RequestProps<T, U>
): UseStore<Request<T, U>> {
  const { fn, onSuccess, onError, postProcess, notFoundResult, once } = props;
  let lastResult: U;

  return create<Request<T, U>>((set) => ({
    loading: false,
    data: null,
    error: null,
    fetch: async (args?: T) => {
      try {
        set({ loading: true, error: null });

        let data = lastResult ? lastResult : await fn(args);

        if (postProcess) {
          data = postProcess(data);
        }

        if (once) {
          lastResult = data;
        }
        if (onSuccess) {
          onSuccess(data);
        }

        set({ loading: false, data });
        return Promise.resolve(data);
      } catch (error) {
        if (error && error.status === 404) {
          if (notFoundResult) {
            const data = notFoundResult(error);

            set({ loading: false, data });
            return Promise.resolve(data);
          }
        }
        if (onError) {
          onError(error);
        }
        set({ loading: false, error });
        return Promise.reject(error);
      }
    },
  }));
}

export function getVisits(props?: Partial<RequestProps>) {
  return request<VisitSearchQuery, VisitResult>({
    ...props,
    ...{
      fn: getVisitRequest,
      postProcess: (r) => {
        if (r) {
          const a: VisitSearchResult = r.result;

          if (a?.entries?.length) {
            a.entries.forEach((v) => {
              v.owner = visitWorkflowOwner(v);
            });
          }
          worksheetQueue.setState({ result: { ...emptyResult, ...r.result } });
        }
        if (r?.errors?.length) {
          if (r.errors[0] !== "No Queue Entries Found") {
            showToast(r.errors.join(","));
          }
        }

        return r;
      },
      notFoundResult: () => ({ result: emptyResult }),
    },
  });
}

type GetNextArgs = {
  searchHandle?: string;
  worksheetId?: string;
  visitId?: string;
  workflow?: string;
};

export const getNextRequest = fetchRequest<GetNextArgs, VisitResult>({
  method: "post",
  body: (args: GetNextArgs) => {
    return worksheetMessage(
      args,
      "getnext",
      "coding-queue",
      args.workflow || currentWorkflow()
    );
  },
});

type VisitCursor = {
  visit: Visit;
  nextVisit: Visit;
  index: number;
  pageIndex: number;
  lastPage: boolean;
  remainingEntries: number;
  searchHandle: string;
};

type WorksheetQueue = {
  result: VisitSearchResult;
  visit: Visit;
  reset: boolean;
  cursor(visit?: Visit): VisitCursor;
  nextResult: () => Promise<void>;
  setVisit(value: Visit): void;
  nextVisit(): Promise<Visit>;
  refreshRefs(): void;
};

export type WorksheetQueueApi = UseStore<WorksheetQueue>;
export const notCodeableError = new Error("Not Codeable");
export const noVisitEntriesError = new Error("No visit entries");
export const endOfVisitListError = new Error("End of visit list.");
export const visitNotFoundError = new Error("Visit not found in result list.");

let worksheetId: string;
export function setLastWorksheetId(id: string) {
  if (id) {
    worksheetId = id;
  }
}
export function getLastWorksheetId() {
  return worksheetId;
}

export function createWorksheetQueueApi(): WorksheetQueueApi {
  function lastWorksheetId(v: Visit): string | undefined {
    if (worksheetId) {
      return worksheetId;
    }
    const i = v?.wsSummaries?.length;
    if (!i) {
      return undefined;
    }
    return v.wsSummaries[i - 1].worksheetId;
  }

  return create<WorksheetQueue>((set, get) => ({
    result: emptyResult,
    visit: null,
    nextResult: null,
    reset: false,
    cursor: (visit) => {
      const result = get().result;
      let { entries, pageNumber, pageSize, totalResults, searchHandle } =
        result;

      if (!entries || !entries.length) {
        throw endOfVisitListError;
      }
      visit = visit || get().visit || entries[0];

      let index = -1;
      entries.forEach((v, i) => {
        if (v.visitId === visit.visitId) {
          index = i;
          visit = v;
        }
      });

      if (index === -1) {
        console.log("no longer using in memory entries, reseting page");
        index = 0;
        pageNumber = 0;
        set({ reset: true, result: { ...result, ...{ pageNumber } } });
      }

      const pageIndex = pageNumber * pageSize + index;
      const lastPage = pageIndex >= totalResults - 1;
      const remainingEntries = entries.length - 1 - index;
      const nextVisit = entries[index + 1];
      return {
        visit,
        nextVisit,
        index,
        pageIndex,
        lastPage,
        remainingEntries,
        searchHandle,
      };
    },
    setVisit: (visit) => {
      // console.log('setVisit', visit)
      if (visit) {
        set({ visit });
      }
    },
    nextVisit: async () => {
      const cursor = get().cursor();
      const { searchHandle, visit } = cursor;
      let remainingEntries = cursor.remainingEntries;
      let lastPage = cursor.lastPage;

      let nextVisit = cursor.nextVisit;

      if (searchHandle) {
        const worksheetId = lastWorksheetId(visit);
        const preferred = await getNextRequest({
          searchHandle,
          worksheetId,
          visitId: visit.visitId,
        });

        //  console.log('preferred visit id', worksheetId, nextVisit);

        if (preferred?.result?.entries?.length) {
          remainingEntries = remainingEntries || 1;
          nextVisit = preferred?.result?.entries[0];
        } else {
          remainingEntries = 0;
          lastPage = true;
        }
      } else {
        console.log("no search handle available");
      }

      if (remainingEntries === 0) {
        if (!lastPage) {
          const fn = get().nextResult;
          if (!fn) {
            throw new Error("No promise to retrieve next visit results.");
          }
          // console.log('Attempting to load Next Visit Page');
          await fn();
          const result = get().result;
          // console.log('Next Visit Results', result.entries[0]);
          get().setVisit(result.entries[0]);
          return Promise.resolve(result.entries[0]);
        } else {
          throw endOfVisitListError;
        }
      } else {
        get().setVisit(nextVisit);
        return Promise.resolve(nextVisit);
      }
    },
    refreshRefs: () => {
      set({ result: { ...get().result } });
    },
  }));
}

export const worksheetQueue = createWorksheetQueueApi();

type FindArgs = LocationDepartmentAccount & {
  worksheetId?: string;
  account?: string;
  visitId?: string;
};

export async function findWorksheetSummary(
  args: FindArgs
): Promise<WorksheetSummary> {
  const query: VisitSearchQuery = {
    locationCode: args.location || "CPMCBVN",
    departmentCode: args.department || "ED",
    filters: {
      predicate: "AND",
      fields: [{ ...worksheetIdField, ...{ textFilter: args?.worksheetId } }],
    },
  };
  const result: VisitSearchResult = (await getVisitRequest(query)).result;
  if (result && result.entries[0]) {
    return Promise.resolve(result.entries[0].wsSummaries[0]);
  }
  return Promise.reject("No worksheet found.");
}

export async function findAccount(args: FindArgs): Promise<Visit> {
  const query: VisitSearchQuery = {
    locationCode: args.location,
    departmentCode: args.department,
    filters: {
      predicate: "AND",
      fields: [{ ...visitAccountField, ...{ textFilter: args.account } }],
    },
  };
  const result: VisitSearchResult = (await getVisitRequest(query)).result;
  let item = null;
  if (result && result.entries.length) {
    item = result.entries[0];
  }
  return item ? Promise.resolve(item) : Promise.reject("No visit found.");
}
